import type React from 'react';
import { useEffect, useState } from 'react';

import { EditRounded as EditIcon } from '@mui/icons-material';
import { IconButton, InputAdornment, TextField } from '@mui/material';
import { PasswordInput, TextInput, useSourceContext, useTranslate } from 'react-admin';
import { useFormContext } from 'react-hook-form';

import type { PasswordInputProps, TextInputProps } from 'react-admin';

interface CommonProps {
  obfuscatedValue?: string | null;
  source: string;
  variant?: 'password' | 'text';
}

type OrdObfuscatedSecretInputProps = CommonProps &
  Omit<TextInputProps, 'source' | 'variant'> &
  Partial<Omit<PasswordInputProps, 'source' | 'variant'>>;

export const OrdObfuscatedSecretInput = ({
  obfuscatedValue = null,
  source,
  variant = 'text',
  ...props
}: OrdObfuscatedSecretInputProps): React.ReactElement => {
  const { setValue } = useFormContext();
  const { getSource } = useSourceContext();
  const translate = useTranslate();
  const composedSource = getSource(source);
  const [editing, setEditing] = useState(obfuscatedValue === null);

  useEffect(() => {
    if (obfuscatedValue !== null) {
      setValue(composedSource, undefined, { shouldDirty: false, shouldValidate: false });
    }
  }, [composedSource, obfuscatedValue, setValue]);

  const doEdit = (): void => {
    if (!editing) {
      setValue(composedSource, '', { shouldValidate: true, shouldDirty: true });
    } else {
      setValue(composedSource, undefined, { shouldValidate: true, shouldDirty: false });
    }

    setEditing(!editing);
  };

  const endAdornment = (
    <InputAdornment position='end'>
      <IconButton onClick={doEdit}>
        <EditIcon />
      </IconButton>
    </InputAdornment>
  );

  if (!editing) {
    return (
      <TextField
        type='text'
        fullWidth
        disabled
        value={obfuscatedValue ?? ''}
        label={typeof props.label === 'string' ? translate(props.label, { _: props.label }) : props.label}
        helperText={
          typeof props.helperText === 'string' ? translate(props.helperText, { _: props.helperText }) : props.helperText
        }
        slotProps={{
          input: { endAdornment },
        }}
      />
    );
  }

  const commonProps = {
    source,
    fullWidth: true,
    ...props,
  };

  return variant === 'password' ? (
    <PasswordInput {...commonProps} autoComplete='new-password' />
  ) : (
    <TextInput {...commonProps} />
  );
};
